import { supabase } from '@/service/supabaseClient';
import type { NotificationRow } from '@/types/notifications';
import { normalizeNotificationRow } from '@/types/notifications';
import { emitNotificationToast } from '@/utils/notifications-toast-bus';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface NotificationRealtimeHandlers {
  onInsert?: (row: NotificationRow) => void;
  onUpdate?: (row: NotificationRow) => void;
  onDelete?: (row: NotificationRow) => void;
  onStatusChange?: (status: string) => void;
}

export interface NotificationsSubscription {
  channel: RealtimeChannel;
  unsubscribe: () => Promise<void>;
  channelName: string;
}

/**
 * Subscribes to realtime changes for public.notifications scoped to a specific user.
 * Rely on RLS and a row-level filter to limit traffic to the target user.
 */
export function subscribeToNotifications(
  userId: string,
  handlers: NotificationRealtimeHandlers,
): NotificationsSubscription {
  const channelName = `public:notifications:user:${userId}`;

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user=eq.${userId}` },
      (payload) => {
        const row = normalizeNotificationRow(payload.new as { data: unknown } & NotificationRow);
        handlers.onInsert?.(row);
        // Emit toast event for new notifications received in realtime
        try {
          emitNotificationToast(row);
        } catch {}
      },
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user=eq.${userId}` },
      (payload) => {
        const row = normalizeNotificationRow(payload.new as { data: unknown } & NotificationRow);
        handlers.onUpdate?.(row);
      },
    )
    .on(
      'postgres_changes',
      { event: 'DELETE', schema: 'public', table: 'notifications', filter: `user=eq.${userId}` },
      (payload) => {
        const row = normalizeNotificationRow(payload.old as { data: unknown } & NotificationRow);
        handlers.onDelete?.(row);
      },
    )
    .subscribe((status) => {
      // Status: 'SUBSCRIBED' | 'CHANNEL_ERROR' | 'TIMED_OUT' | 'CLOSED'
      handlers.onStatusChange?.(status);
    });

  return {
    channel,
    channelName,
    unsubscribe: async () => {
      await supabase.removeChannel(channel);
    },
  };
}
